/**
 * GhostNode - Suggested nodes from Gap Analysis
 *
 * These are translucent nodes that the user can approve or dismiss
 */

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Sparkles, Check, X } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';

const GhostNode = memo(({ data, id }) => {
  const { content, title, rationale } = data;
  const { approveGhostNode, dismissGhostNode } = useMapStore();

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="w-56 group"
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-purple-400/50 !border-0"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !bg-purple-400/50 !border-0"
      />

      {/* Ghost card - translucent styling */}
      <div className="relative rounded-xl overflow-hidden bg-purple-500/10 border border-dashed border-purple-400/40 backdrop-blur-sm">
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/5 to-transparent animate-shimmer" />

        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-purple-400/20">
          <Sparkles size={14} className="text-purple-400" />
          <span className="flex-1 text-sm font-medium text-purple-300 truncate">
            {title || 'Suggested Node'}
          </span>
        </div>

        {/* Content */}
        <div className="p-3">
          <p className="text-sm text-purple-200/80 leading-relaxed">
            {content?.substring(0, 100)}
            {content?.length > 100 && '...'}
          </p>

          {/* Rationale */}
          {rationale && (
            <p className="mt-2 text-xs text-purple-400/60 italic">
              💡 {rationale}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex border-t border-purple-400/20">
          <button
            onClick={() => approveGhostNode(data)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-green-400 hover:bg-green-500/10 transition-colors"
          >
            <Check size={12} />
            <span>Approve</span>
          </button>
          <div className="w-px bg-purple-400/20" />
          <button
            onClick={() => dismissGhostNode(data)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <X size={12} />
            <span>Dismiss</span>
          </button>
        </div>
      </div>

      {/* Ghost label */}
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-purple-500/20 rounded text-[10px] text-purple-300 whitespace-nowrap">
        AI Suggestion
      </div>
    </motion.div>
  );
});

GhostNode.displayName = 'GhostNode';

export default GhostNode;
